import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchArtistQuote } from "../api/artistQuoteService";

const localeFor = (language) => {
  if (language?.startsWith("de")) return "de-DE";
  if (language?.startsWith("en")) return "en-GB";
  return "fr-FR";
};

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function MediumBreakdown({ artistName }) {
  const { t, i18n } = useTranslation();
  const [mediums, setMediums] = useState([]);
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    async function loadMediums() {
      const data = await fetchArtistQuote(artistName);
      if (!data) return;

      const counts = {};
      data.forEach((item) => {
        const key = String(item.medium || "").toLowerCase();
        if (!counts[key]) counts[key] = [];
        counts[key].push(item);
      });

      const breakdown = Object.entries(counts).map(([key, values]) => {
        const avg = values.reduce((sum, item) => sum + Number.parseFloat(item.average_value || 0), 0) / values.length;
        return {
          medium: key,
          average: avg,
          count: values.length,
        };
      });

      setMediums(breakdown);
    }
    loadMediums();
  }, [artistName]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

  const mediumLabel = (medium) => {
    const key = `artistQuoteTools.mediums.${normalizeKey(medium)}`;
    return i18n.exists(key) ? t(key) : medium;
  };

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold mb-4">🎨 {t("artistQuoteTools.mediumBreakdown.title")}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {mediums.map((item, index) => (
          <div key={`${item.medium}-${index}`} className="border rounded p-4 shadow-sm bg-white">
            <p className="font-medium capitalize">{mediumLabel(item.medium)}</p>
            <p className="text-sm text-gray-700">
              {t("artistQuoteTools.mediumBreakdown.averagePrice", { value: formatCurrency(item.average) })}
            </p>
            <p className="text-xs text-gray-500">
              {t("artistQuoteTools.mediumBreakdown.analyzed", { count: item.count })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
